import { useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import { useCustodianAccounts } from './useCustodianAccounts';
import {
  selectApexAccounts,
  selectCustodianHoldingsById,
  selectCustodianTransfersById,
} from '@/store/features/apex/apex-selectors';
import { ApexAccount } from '@/types/wealth';
import { ASSET_LIABILITY } from '@/util/planning-util';

/**
 * ApexAccount[] for this investor's Apex accounts with their holdings
 * attached — used by the holdings-driven tabs (AllocationTab's pie chart,
 * AllHoldingsTab's table, which read only `.id` and `.holdings`) and by the
 * wealth page's account collapse, which also shows each account's name,
 * type, and balance in its header.
 *
 * For a legacy Apex firm this is exactly selectApexAccounts, unchanged. For
 * a firm-data + Apex firm (where selectApexAccounts is always empty — see
 * useCustodianAccounts), it rebuilds the equivalent list from firm-data's
 * own blended accounts: real name/type/balance from the firm-data account,
 * holdings from custodianHoldingsById.
 *
 * Every entry is keyed by the Apex custodian account id, in both cases.
 * That is deliberate — deposits/withdrawals still go to fo-apex with that
 * id, and holdings fetch uses the same Apex id as the caller (mapped to
 * firm-data's account id inside fetchAccountHoldings when the flag is on).
 * `type` is whichever vocabulary its
 * source uses (Apex goal vs firm-data account_type), so display it through
 * formatAccountType, never Apex's own accountTypeToName.
 *
 * Apex-only fields firm-data does not carry (allocation/risk score, IRA
 * contribution and distribution constraints) stay undefined on firm-data
 * entries, and the UI hides what depends on them.
 */
export function useApexHoldingsAccounts(): ApexAccount[] {
  const legacyAccounts = useAppSelector(selectApexAccounts);
  const { isLegacy, accounts } = useCustodianAccounts();
  const custodianHoldingsById = useAppSelector(selectCustodianHoldingsById);
  const custodianTransfersById = useAppSelector(selectCustodianTransfersById);

  return useMemo(() => {
    if (isLegacy) {
      return legacyAccounts;
    }

    return accounts.map(
      (account): ApexAccount => ({
        id: account.id,
        name: account.name,
        type: account.type as ApexAccount['type'],
        total_equity: account.totalEquity,
        asset_or_liability: ASSET_LIABILITY.ASSET,
        // Optional access on purpose: this map was added after the apex
        // slice shipped, so a rehydrated older state can arrive without it,
        // and an undefined lookup here would crash the whole wealth page.
        holdings: custodianHoldingsById?.[account.id],
        // Same optional access, and the same reason the wealth page needs it
        // here at all: the collapse decides whether to fetch a transfer list
        // from account.transferList, so leaving it undefined both hides the
        // transfers and re-fetches them on every expand.
        transferList: custodianTransfersById?.[account.id],
      }),
    );
  }, [isLegacy, legacyAccounts, accounts, custodianHoldingsById, custodianTransfersById]);
}

export default useApexHoldingsAccounts;
